<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRentalFormProperties',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    editable: {
      type: Boolean,
      required: false,
      default: true,
    },
    rentalId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = async () => {
    return $fn.value.fetch('list_rental_property', {
      parameters: { rental_id: props.rentalId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: { initialLoadSpinner: true },
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.editable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editable');
    }
  );
  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );

  const inst_evh_2095089031469157_close_w1b8gfa6y_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_32lueksov_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalProperty',
      props: {
        rentalId: props.rentalId,
      },
    };
  };
  const inst_evh_6853569216922577_close_5kzt3n229_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_6853569216922577_showModal_yhq8nvxj1_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalProperty',
      props: {
        rentalId: props.rentalId,
        resourceId: $event.data.item.id,
      },
    };
  };
  const act_6492569393195423_request_dqvvm1tjb_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_rental_property',
      data: { ...$event.data.prop, rental_id: props.rentalId },
    };
  };
  const act_6492569393195423_request_dqvvm1tjb_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_reloadComponentData_dvclyvhg7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_9bsex08zt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_dvclyvhg7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_9bsex08zt],
    event: 'reload',
  });
  const act_2095089031469157_reloadComponentData_p4dj0708g =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2095089031469157_close_w1b8gfa6y =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadComponentData_p4dj0708g,
      event: 'close',
      when: inst_evh_2095089031469157_close_w1b8gfa6y_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_2095089031469157_showModal_32lueksov =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_32lueksov_getActionArgs,
      events: [inst_evh_2095089031469157_close_w1b8gfa6y],
    });
  const inst_evh_2095089031469157_clickPreventStop_txnef7emm =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_32lueksov,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_txnef7emm],
      event: 'click.prevent.stop',
    });
  const act_6853569216922577_reloadComponentData_oy8f1v0wt =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6853569216922577_close_5kzt3n229 =
    new actionTypes.EventHandlerImpl({
      action: act_6853569216922577_reloadComponentData_oy8f1v0wt,
      event: 'close',
      when: inst_evh_6853569216922577_close_5kzt3n229_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_6853569216922577_showModal_yhq8nvxj1 =
    new actionTypes.ShowModalAction({
      actionArgs: act_6853569216922577_showModal_yhq8nvxj1_getActionArgs,
      events: [inst_evh_6853569216922577_close_5kzt3n229],
    });
  const inst_evh_6853569216922577_clickPreventStop_bosnkxg2c =
    new actionTypes.EventHandlerImpl({
      action: act_6853569216922577_showModal_yhq8nvxj1,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_6853569216922577_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6853569216922577_clickPreventStop_bosnkxg2c],
      event: 'click.prevent.stop',
    });
  const act_6492569393195423_reloadComponentData_6x0odvyci =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6492569393195423_success_mk903lrms =
    new actionTypes.EventHandlerImpl({
      action: act_6492569393195423_reloadComponentData_6x0odvyci,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_6492569393195423_request_dqvvm1tjb = new actionTypes.RequestAction({
    actionArgs: act_6492569393195423_request_dqvvm1tjb_getActionArgs,
    confirm: act_6492569393195423_request_dqvvm1tjb_getConfirm,
    events: [inst_evh_6492569393195423_success_mk903lrms],
  });
  const inst_evh_6492569393195423_clickPreventStop_t6r8e1waf =
    new actionTypes.EventHandlerImpl({
      action: act_6492569393195423_request_dqvvm1tjb,
      event: 'click.prevent.stop',
      displayName: 'request',
    });
  const inst_evh_6492569393195423_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6492569393195423_clickPreventStop_t6r8e1waf],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_9bsex08zt,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_w1b8gfa6y,
    inst_evh_2095089031469157_clickPreventStop_txnef7emm,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_6853569216922577_close_5kzt3n229,
    inst_evh_6853569216922577_clickPreventStop_bosnkxg2c,
    inst_evh_6853569216922577_clickPreventStop,
    inst_evh_6492569393195423_success_mk903lrms,
    inst_evh_6492569393195423_clickPreventStop_t6r8e1waf,
    inst_evh_6492569393195423_clickPreventStop,
  };

  const evh_7315092382398562_reload_9bsex08zt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_9bsex08zt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_w1b8gfa6y = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_w1b8gfa6y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_txnef7emm = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_txnef7emm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6853569216922577_close_5kzt3n229 = async (event, scope) => {
    return await actions.inst_evh_6853569216922577_close_5kzt3n229.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6853569216922577_clickPreventStop_bosnkxg2c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6853569216922577_clickPreventStop_bosnkxg2c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6853569216922577_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6853569216922577_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6492569393195423_success_mk903lrms = async (event, scope) => {
    return await actions.inst_evh_6492569393195423_success_mk903lrms.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6492569393195423_clickPreventStop_t6r8e1waf = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6492569393195423_clickPreventStop_t6r8e1waf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6492569393195423_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6492569393195423_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    loading-spinner
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('RentalFormProperties', 'Properties') }}
          </strong>
          <ItpButton
            :text="gettext('Add property...')"
            size="sm"
            variant="light"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup v-if="loaded">
        <BListGroupItem class="card-body text-center" v-if="!items.length">
          <span>
            {{ pgettext('RentalFormProperties', 'No properties.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-column"
          v-for="item in items"
        >
          <div class="w-100">
            <div class="d-flex flex-row">
              <div class="px-2">
                {{ item.property_unit.full_name }}
              </div>
              <div :id="item.property_unit.unit_type" class="px-2">
                {{ item.property_unit.unit_type }}
              </div>
            </div>
            <div class="d-flex flex-row my-1">
              <div class="px-2">
                {{ item.valid_from }}
              </div>
              <div class="px-2">
                {{ item.valid_to }}
              </div>
            </div>
            <div class="d-flex flex-row">
              <ItpBadge
                variant="secondary"
                class="mx-2 shadow-none"
                v-if="item.locked"
              >
                <ItpIcon fa="lock" class="px-2"> </ItpIcon>
                <span>
                  {{ gettext('Locked') }}
                </span>
              </ItpBadge>
              <ItpBadge
                :text="gettext('Inactive')"
                variant="warning"
                class="mx-2 shadow-none"
                v-if="item.inactive"
              >
              </ItpBadge>
            </div>
          </div>
          <div class="ms-2 d-flex flex-row">
            <ItpButton
              variant="default"
              size="sm"
              icon="fa-edit"
              v-if="editable"
              @click.prevent.stop="
                evh_6853569216922577_clickPreventStop($event, { item })
              "
            >
            </ItpButton>
            <ItpButton
              variant="default"
              size="sm"
              icon="fa-trash"
              v-if="editable && !item.locked"
              @click.prevent.stop="
                evh_6492569393195423_clickPreventStop($event, { item })
              "
            >
            </ItpButton>
          </div>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
